<template>
  <div class="w-full p-2 sm:p-4 font-normal text-vp-base-900 text-base">
    <label
      v-for="checkbox in checkboxes"
      :key="checkbox.key"
      class="checkbox-label"
      :class="{required: checkbox.required}"
    >
      <input
        type="checkbox"
        :checked="getCheckboxState(checkbox.key)"
        class="checkbox"
        @click="toggleCheckboxState(checkbox.key)"
      >
      <span class="label-text">
        {{ checkbox.label }}
        <span
          v-if="checkbox.required"
          class="label-text text-xs text-vp-base-600"
        >({{ $gettext('required') }})</span>
      </span>
      <button
        v-if="checkbox.description"
        type="button"
        class="p-1"
        @click="showCheckboxDetailsFor(checkbox)"
      >
        <iconify-icon
          icon="mdi:help-circle-outline"
          class="text-lg"
        />
      </button>
    </label>
  </div>

  <VPModal
    v-model:show="showModal"
    show-close-button
    hide-actions
    :title="modalTitle"
  >
    <div v-html="modalBody" />
  </VPModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import VPModal from '@/components/form/VPModal.vue';
import type { Checkout } from '@vp/common';
import { useGettext } from 'vue3-gettext';

const emit = defineEmits<{
  (e: 'update', v: Record<string, boolean>): void;
  (e: 'requiredSatisfied', v: boolean): void;
}>();

const props = defineProps<{
  checkboxes: Checkout.CheckoutCheckboxConfig.CheckoutCheckboxConfig[];
}>();

const { $gettext } = useGettext();

const checkboxValues = ref<Record<string, boolean>>({
  // TODO: These values should be provided by the server
  newsletter: true,
  sms_newsletter: true,
});
const showModal = ref(false);
const modalTitle = ref<string | undefined>('');
const modalBody = ref<string | undefined>('');

const getCheckboxState = (key: string) => checkboxValues.value[key] || false;

const toggleCheckboxState = (key: string) => {
  const currentValue = checkboxValues.value[key] || false;

  checkboxValues.value = {
    ...checkboxValues.value,
    [key]: !currentValue,
  };
};

const requiredCheckboxesChecked = computed(() =>
  props.checkboxes.every(({ key, required }) => getCheckboxState(key) || !required),
);

const showCheckboxDetailsFor = (checkbox: Checkout.CheckoutCheckboxConfig.CheckoutCheckboxConfig): void => {
  showModal.value = true;
  modalTitle.value = checkbox.descriptionTitle;
  modalBody.value = checkbox.description;
};

watch(
  checkboxValues,
  newValue => {
    emit('update', { ...newValue });
  },
  { immediate: true },
);

watch(
  () => requiredCheckboxesChecked.value,
  newValue => {
    emit('requiredSatisfied', newValue);
  },
);
</script>
